export type FinanceIncomeSortKey =
  | "status"
  | "invoiceNumber"
  | "client"
  | "installation"
  | "concept"
  | "taxableBase"
  | "invoiceTotal"
  | "dueDate";

export type FinanceIncomeSortDirection = "asc" | "desc";

export type FinanceIncomeSort = {
  key: FinanceIncomeSortKey;
  direction: FinanceIncomeSortDirection;
};

export type FinanceIncomeSortValue = string | number | null | undefined;

export function toggleFinanceIncomeSort(
  current: FinanceIncomeSort | null,
  key: FinanceIncomeSortKey,
): FinanceIncomeSort {
  return current?.key === key
    ? { key, direction: current.direction === "desc" ? "asc" : "desc" }
    : { key, direction: "desc" };
}

export function sortFinanceIncomeRows<T>(
  rows: readonly T[],
  sort: FinanceIncomeSort,
  getValue: (row: T, key: FinanceIncomeSortKey) => FinanceIncomeSortValue,
): T[] {
  const collator = new Intl.Collator("es", {
    numeric: true,
    sensitivity: "base",
  });

  return rows
    .map((value, index) => ({ value, index, sortValue: getValue(value, sort.key) }))
    .sort((left, right) => {
      const a = left.sortValue ?? "";
      const b = right.sortValue ?? "";
      const aBlank = typeof a === "string" && a.trim() === "";
      const bBlank = typeof b === "string" && b.trim() === "";

      // Keep missing invoice fields at the end in either sort direction.
      if (aBlank !== bBlank) return aBlank ? 1 : -1;

      const comparison =
        typeof a === "number" && typeof b === "number"
          ? a - b
          : collator.compare(String(a), String(b));

      return (
        (sort.direction === "desc" ? -comparison : comparison) ||
        left.index - right.index
      );
    })
    .map(({ value }) => value);
}
